import { Dayjs } from "dayjs"
import { useState } from "react"

import {
    AssessEVACDeviceDetails,
    AssessEVDCDeviceDetails,
} from "../../../../components/devices/lct/types/providerTypes"
import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { useV2gCommentRouting } from "../../../../hooks/useV2gCommentRouting"
import {
    dayJsToStrForTtrApi,
    snackbar,
} from "../../../../utils/general/generalUtils"
import { scrollToTop } from "../../../../utils/submitApplication/submitApplicationUtils"
import { postClientProtected } from "../../clients/clients"
import {
    LCT_ASSESS_DEVICE,
    TTR_ASSESS_DEVICE,
    V2G_ASSESS_LCT_DEVICE,
} from "../../urls/private"

interface IAssessDeviceHook {
    isSending: boolean
    error: IAppError | undefined
    postTtrResponse: (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        complianceStatusID: number
    ) => Promise<void>
    postLctResponse: (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        dataset: Datasets,
        body: AssessEVACDeviceDetails | AssessEVDCDeviceDetails
    ) => Promise<void>
}

/**
 * Provides functions and logic for posting a submit assessment request
 * on a TTR or LCT device. Pass `isV2gDevice` when the device is a V2G EVCP
 * so that V2G assessors are routed to the `/v2g/v1/` endpoints.
 * @returns {IAssessDeviceHook} - loading and error states.
 */
const useAssessDevice = (
    onSuccess: () => void,
    isV2gDevice = false
): IAssessDeviceHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)
    const useAsV2gAssessor = useV2gCommentRouting(isV2gDevice)

    const postTtrResponse = async (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        complianceStatusID: number
    ): Promise<void> => {
        try {
            setIsSending(true)

            // Build response
            const body = {
                DeviceRef: deviceRef,
                BasedOnPublishedDate: dayJsToStrForTtrApi(basedOnPublishedDate),
                ComplianceStatusID: complianceStatusID,
            }
            await postClientProtected(TTR_ASSESS_DEVICE, body)
            snackbar("Assessment submitted successfully", "success")
            setError(undefined)
            onSuccess()
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    const postLctResponse = async (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        dataset: Datasets,
        assessedDetails: AssessEVACDeviceDetails | AssessEVDCDeviceDetails
    ): Promise<void> => {
        try {
            setIsSending(true)
            // Build response
            const body = {
                DeviceRef: deviceRef,
                BasedOnPublishedDate: dayJsToStrForTtrApi(basedOnPublishedDate),
                Dataset: dataset,
                ...assessedDetails,
            }
            const url = useAsV2gAssessor
                ? V2G_ASSESS_LCT_DEVICE
                : LCT_ASSESS_DEVICE
            await postClientProtected(url, body)
            snackbar("Assessment submitted successfully", "success")
            setError(undefined)
            onSuccess()
            scrollToTop(document)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        postTtrResponse,
        postLctResponse,
    }
}

export default useAssessDevice
